import {
  LOGO_URL,
  ORGANIZATION_NAME,
  ORGANIZATION_URL,
} from '@portfolio/configs'
import { PageBuilderSection, Position } from '@portfolio/models'
import { isNonEmptyString } from '@portfolio/utils'
import { isLinkHref } from '@portfolio/components'

const DEFAULT_JOB_DESCRIPTION =
  'See full job details, responsibilities, and requirements on the application page.'
const DEFAULT_REMOTE_COUNTRY = 'Poland'
const DEFAULT_JOB_LOCALITY = 'Warsaw'
const DEFAULT_JOB_COUNTRY_CODE = 'PL'
const EMPLOYMENT_TYPES = ['FULL_TIME', 'PART_TIME'] as const

type JsonLdObject = Record<string, unknown>

export type JobPostingJsonLdScript = {
  key: string
  jsonLd: JsonLdObject
}

type OpenPositionsSection = Extract<
  PageBuilderSection,
  { _type: 'sectionOpenPositions' }
>

const isOpenPositionsSection = (
  section: PageBuilderSection,
): section is OpenPositionsSection => section._type === 'sectionOpenPositions'

const getDatePart = (value: string) => value.split('T')[0]

const getDatePosted = (positionDatePosted?: string, pageUpdatedAt?: string) => {
  if (isNonEmptyString(positionDatePosted)) {
    return getDatePart(positionDatePosted)
  }

  if (isNonEmptyString(pageUpdatedAt)) {
    return getDatePart(pageUpdatedAt)
  }

  return new Date().toISOString().split('T')[0]
}

const getDescription = (
  positionJsonLdDescription?: string,
  positionDescription?: string,
  sectionDescription?: string,
) => {
  if (isNonEmptyString(positionJsonLdDescription)) {
    return positionJsonLdDescription.trim()
  }
  if (isNonEmptyString(positionDescription)) return positionDescription.trim()
  if (isNonEmptyString(sectionDescription)) return sectionDescription.trim()
  return DEFAULT_JOB_DESCRIPTION
}

const getEmploymentType = (employmentType?: string) => {
  if (!isNonEmptyString(employmentType)) return 'FULL_TIME'
  return EMPLOYMENT_TYPES.includes(
    employmentType as typeof EMPLOYMENT_TYPES[number],
  )
    ? employmentType
    : 'FULL_TIME'
}

const getBaseSalary = (baseSalary?: number): JsonLdObject | undefined => {
  if (typeof baseSalary !== 'number' || Number.isNaN(baseSalary))
    return undefined
  if (baseSalary <= 0) return undefined

  return {
    '@type': 'MonetaryAmount',
    currency: 'PLN',
    value: {
      '@type': 'QuantitativeValue',
      unitText: 'MONTH',
      value: baseSalary,
    },
  }
}

const createJobPostingJsonLd = (
  position: Position,
  sectionDescription: string | undefined,
  pageUpdatedAt?: string,
): JsonLdObject => {
  const jsonLd: JsonLdObject = {
    '@context': 'https://schema.org',
    '@type': 'JobPosting',
    title: position.title,
    description: getDescription(
      position.jsonLdDescription,
      position.description,
      sectionDescription,
    ),
    datePosted: getDatePosted(position.datePosted, pageUpdatedAt),
    hiringOrganization: {
      '@type': 'Organization',
      name: ORGANIZATION_NAME,
      sameAs: ORGANIZATION_URL,
      logo: LOGO_URL,
    },
    jobLocationType: 'TELECOMMUTE',
    jobLocation: {
      '@type': 'Place',
      address: {
        '@type': 'PostalAddress',
        addressLocality: DEFAULT_JOB_LOCALITY,
        addressCountry: DEFAULT_JOB_COUNTRY_CODE,
      },
    },
    applicantLocationRequirements: {
      '@type': 'Country',
      name: DEFAULT_REMOTE_COUNTRY,
    },
    employmentType: getEmploymentType(position.employmentType),
    directApply: false,
  }

  if (isNonEmptyString(position.validThrough)) {
    jsonLd.validThrough = position.validThrough
  }

  const baseSalary = getBaseSalary(position.baseSalary)
  if (baseSalary) {
    jsonLd.baseSalary = baseSalary
  }

  const ctaLink = position.ctaLink

  if (ctaLink && isLinkHref(ctaLink) && isNonEmptyString(ctaLink.href)) {
    jsonLd.url = ctaLink.href
  }

  return jsonLd
}

export const createJobPostingScripts = ({
  pageBuilder,
  pageUpdatedAt,
}: {
  pageBuilder?: PageBuilderSection[]
  pageUpdatedAt?: string
}): JobPostingJsonLdScript[] =>
  pageBuilder?.filter(isOpenPositionsSection).flatMap((section) =>
    (section.positions || []).map((position) => ({
      key: `${section._key}-${position._key}`,
      jsonLd: createJobPostingJsonLd(
        position,
        section.description,
        pageUpdatedAt,
      ),
    })),
  ) || []
